/**
 * Zustand store for onboarding flow state.
 *
 * Manages onboarding dialog visibility, screen navigation, and checklist
 * completion tracking. Checklist state persists to localStorage with Zod
 * schema validation to prevent crashes from stale/corrupted data.
 *
 * checklistDismissed is local-only (for animation) — permanent dismissal
 * lives on the User model (user.checklist_dismissed).
 */

import { z } from "zod";
import { create } from "zustand";
import { type ChecklistItem, DEFAULT_CHECKLIST } from "@/constants/onboarding";

export type { ChecklistItem };

interface OnboardingState {
	// State
	isOpen: boolean;
	currentScreen: number;
	completed: boolean;
	checklist: ChecklistItem[];
	checklistDismissed: boolean; // local-only flag for immediate UI hide during animation

	// Actions
	openOnboarding: () => void;
	closeOnboarding: () => void;
	nextScreen: () => void;
	prevScreen: () => void;
	goToStep: (step: number) => void;
	skipOnboarding: () => void;
	toggleChecklistItem: (id: string) => void;
	dismissChecklist: () => void;
	resetOnboarding: () => void;
}

// Base screens count (welcome, task creation, integrations, triggers)
const BASE_SCREENS_COUNT = 4;

// Current version for localStorage data structure
const ONBOARDING_VERSION = 3;

// Schema for validating localStorage data — prevents crashes from stale/malformed data
const onboardingSchema = z.object({
	version: z.literal(ONBOARDING_VERSION),
	completed: z.boolean(),
	checklist: z.array(z.object({ id: z.string(), label: z.string(), completed: z.boolean() })),
});

// Load from localStorage with Zod validation
const loadOnboardingState = () => {
	if (typeof window === "undefined") return null;

	try {
		const stored = localStorage.getItem("onboarding");
		if (!stored) return null;

		const result = onboardingSchema.safeParse(JSON.parse(stored));
		if (!result.success) {
			localStorage.removeItem("onboarding");
			return null;
		}

		return result.data;
	} catch {
		return null;
	}
};

// Save to localStorage (checklist_dismissed now lives on the User model, not here)
const saveOnboardingState = (completed: boolean, checklist: ChecklistItem[]) => {
	if (typeof window === "undefined") return;

	try {
		localStorage.setItem(
			"onboarding",
			JSON.stringify({ version: ONBOARDING_VERSION, completed, checklist })
		);
	} catch {
		// Ignore localStorage errors
	}
};

// Initialize from localStorage or defaults
const initialState = loadOnboardingState();

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
	// Initial state
	isOpen: false,
	currentScreen: 0,
	completed: initialState?.completed ?? false,
	checklist: initialState?.checklist ?? DEFAULT_CHECKLIST,
	checklistDismissed: false, // always starts false — backend-driven via user.checklist_dismissed

	// Open onboarding dialog
	openOnboarding: () => {
		set({ isOpen: true, currentScreen: 0 });
	},

	// Close onboarding dialog
	closeOnboarding: () => {
		set({ isOpen: false });
	},

	// Navigate to next screen
	nextScreen: () => {
		const { currentScreen, checklist } = get();
		// Calculate total screens: base screens + checklist screen if all items completed
		const allChecklistItemsCompleted = checklist.every((item) => item.completed);
		const totalScreens = allChecklistItemsCompleted ? BASE_SCREENS_COUNT + 1 : BASE_SCREENS_COUNT;

		if (currentScreen < totalScreens - 1) {
			set({ currentScreen: currentScreen + 1 });
		} else {
			// Last screen - mark as completed and close
			set({ completed: true, isOpen: false });
			saveOnboardingState(true, checklist);
		}
	},

	// Navigate to previous screen
	prevScreen: () => {
		const { currentScreen } = get();
		if (currentScreen > 0) {
			set({ currentScreen: currentScreen - 1 });
		}
	},

	// Go to specific step
	goToStep: (step: number) => {
		const { checklist } = get();
		// Calculate total screens: base screens + checklist screen if all items completed
		const allChecklistItemsCompleted = checklist.every((item) => item.completed);
		const totalScreens = allChecklistItemsCompleted ? BASE_SCREENS_COUNT + 1 : BASE_SCREENS_COUNT;

		if (step >= 0 && step < totalScreens) {
			set({ currentScreen: step });
		}
	},

	// Skip onboarding
	skipOnboarding: () => {
		const { checklist } = get();
		set({ completed: true, isOpen: false });
		saveOnboardingState(true, checklist);
	},

	// Toggle checklist item completion
	toggleChecklistItem: (id: string) => {
		const { checklist, completed } = get();
		const updated = checklist.map((item) =>
			item.id === id ? { ...item, completed: !item.completed } : item
		);
		set({ checklist: updated });
		saveOnboardingState(completed, updated);
	},

	// Dismiss checklist widget (local-only, backend persistence happens in the widget)
	dismissChecklist: () => {
		set({ checklistDismissed: true });
	},

	// Reset onboarding (for testing)
	resetOnboarding: () => {
		set({
			completed: false,
			currentScreen: 0,
			checklist: DEFAULT_CHECKLIST,
			checklistDismissed: false,
		});
		saveOnboardingState(false, DEFAULT_CHECKLIST);
	},
}));
